import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Colorful, ColorResult } from '@uiw/react-color';
import { Box, Typography, IconButton, Slider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as DrawSVG } from '../assets/draw.svg';
import { hsvaToHexa, hexToHsva } from '@uiw/color-convert';
import ColorHistory from './ColorHistory';
import { useColorHistoryContext } from '../contexts/ColorHistoryContext';

// Pencil size presets for quick selection
const PENCIL_PRESETS = [
	{ width: 1, height: 1 },
	{ width: 2, height: 2 },
	{ width: 2, height: 1 },
	{ width: 1, height: 2 },
	{ width: 5, height: 5 },
];

// Pencil tool sub-views
type PENCIL_VIEW = 'main' | 'size' | 'color';

export type ToolsPencilProps = {
	p5: () => any; // p5 instance
};

function ToolsPencil({ p5 }: ToolsPencilProps) {
	const [pencilView, setPencilView] = useState<PENCIL_VIEW>('main');
	const [hsva, setHsva] = useState({ h: 0, s: 0, v: 68, a: 1 });
	const [pencilSizeX, setPencilSizeX] = useState(1);
	const [pencilSizeY, setPencilSizeY] = useState(1);

	// Color history context
	const { colorHistory, selectColorFromHistory } = useColorHistoryContext();

	useEffect(() => {
		// Get initial pencil colour
		const initialColor = p5()?.getPencilColourHex();
		if (initialColor) {
			setHsva(hexToHsva(initialColor));
		}
		// Get initial pencil size
		const size = p5()?.getPensilSize();
		if (size) {
			setPencilSizeX(size.x);
			setPencilSizeY(size.y);
		}
	}, [p5]);

	const updateColor = (color: ColorResult) => {
		setHsva(color.hsva);
		// console.log('color', color.hexa);
		p5().setPencilColour(color.hexa);
	};

	const handleColorHistorySelect = (color: string) => {
		// Convert hex to hsva and update state
		const newHsva = hexToHsva(color);
		setHsva(newHsva);
		// Update p5 pencil color
		p5().setPencilColour(color);
		// Move selected color to front of history (it's already been used before)
		selectColorFromHistory(color);
	};

	const updatePencilSize = (width: number, height: number) => {
		// Ensure sizes are within limits (1-10)
		const newWidth = Math.min(Math.max(1, width), 10);
		const newHeight = Math.min(Math.max(1, height), 10);

		setPencilSizeX(newWidth);
		setPencilSizeY(newHeight);
		p5().setPensilSize(newWidth, newHeight);
	};

	const handleSliderChange = (axis: 'x' | 'y', value: number) => {
		if (axis === 'x') {
			updatePencilSize(value, pencilSizeY);
		} else {
			updatePencilSize(pencilSizeX, value);
		}
	};

	const selectPresetSize = (width: number, height: number) => {
		updatePencilSize(width, height);
	};

	// Render a grid of cells for the pencil size preview
	const renderPencilSizeGrid = (width: number, height: number, cellSize = 6) => {
		// Create an array of cells
		const cells = [];
		for (let y = 0; y < height; y++) {
			for (let x = 0; x < width; x++) {
				cells.push(
					<div
						key={`${x}-${y}`}
						style={{
							width: cellSize,
							height: cellSize,
							backgroundColor: hsvaToHexa(hsva) || '#fff',
							border: '0.1px solid #aaa8',
							boxSizing: 'border-box',
						}}
					/>,
				);
			}
		}

		return (
			<div
				style={{
					display: 'grid',
					gap: 0,
					borderRadius: '2px',
					gridTemplateColumns: `repeat(${width}, ${cellSize}px)`,
					gridTemplateRows: `repeat(${height}, ${cellSize}px)`,
				}}
			>
				{cells}
			</div>
		);
	};

	return (
		<>
			{pencilView === 'main' && (
				<Box flex={1}>
					<Typography mx={2} mt={1} mb={0} component="h5" fontWeight={700} fontSize={12}>
						Pencil
					</Typography>

					<Box display="flex" alignItems="center" p={1} m={1} mr={0}>
						<Box display="flex" alignItems="center" flex={1} mr={1}>
							<PencilIconWrapper>
								<DrawSVG style={{ width: 30, height: 30 }} />
							</PencilIconWrapper>
							<Box ml={2} display="flex" alignItems="center">
								{renderPencilSizeGrid(pencilSizeX, pencilSizeY, 6)}
								<Typography ml={1} fontSize={11} fontWeight={600}>
									{pencilSizeX} x {pencilSizeY}
								</Typography>
							</Box>
						</Box>

						<Box display="flex" flexDirection="column" alignItems="stretch">
							<ActionButton onClick={() => setPencilView('size')}>Size</ActionButton>
							<ActionButton onClick={() => setPencilView('color')} style={{ marginTop: '6px' }}>
								Color
							</ActionButton>
						</Box>
					</Box>
				</Box>
			)}

			{pencilView === 'size' && (
				<Box flex={1}>
					<Box display="flex" justifyContent="space-between" alignItems="top">
						<Typography mx={2} mt={1} mb={0} component="h5" fontWeight={700} fontSize={12}>
							Specify Pencil Size
						</Typography>
						<IconButton size="small" onClick={() => setPencilView('main')}>
							<CloseIcon sx={{ color: '#fff' }} />
						</IconButton>
					</Box>

					<Box px={5} mt={1}>
						<PresetGrid>
							{PENCIL_PRESETS.map((preset, index) => (
								<PresetItem key={index} onClick={() => selectPresetSize(preset.width, preset.height)} className={pencilSizeX === preset.width && pencilSizeY === preset.height ? 'selected' : ''}>
									{renderPencilSizeGrid(preset.width, preset.height, 5)}
								</PresetItem>
							))}
						</PresetGrid>
					</Box>

					<Box px={1} pt={2}>
						<Box flex={1}>
							<SliderRow>
								<SliderLabel>Width</SliderLabel>
								<StyledSlider value={pencilSizeX} onChange={(_, value) => handleSliderChange('x', value as number)} min={1} max={10} step={1} marks size="small" />
								<SizeIndicator>{pencilSizeX}</SizeIndicator>
							</SliderRow>
						</Box>

						<Box flex={1}>
							<SliderRow>
								<SliderLabel>Height</SliderLabel>
								<StyledSlider value={pencilSizeY} onChange={(_, value) => handleSliderChange('y', value as number)} min={1} max={10} step={1} marks size="small" />
								<SizeIndicator>{pencilSizeY}</SizeIndicator>
							</SliderRow>
						</Box>
					</Box>
				</Box>
			)}

			{pencilView === 'color' && (
				<ColourWrapper>
					<Box display="flex" justifyContent="space-between" alignItems="top">
						<Typography mx={2} mt={1} mb={0} component="h5" fontWeight={700} fontSize={12}>
							Specify Pencil Colour
						</Typography>
						<IconButton size="small" onClick={() => setPencilView('main')}>
							<CloseIcon sx={{ color: '#fff' }} />
						</IconButton>
					</Box>

					<Box px={1} pt={1}>
						<ColorHistory colors={colorHistory} onColorSelect={handleColorHistorySelect} currentColor={hsvaToHexa(hsva)} />
					</Box>

					<Colorful style={{ padding: 20, width: '100%' }} color={hsva} onChange={updateColor} />
				</ColourWrapper>
			)}
		</>
	);
}

export default ToolsPencil;

const PencilIconWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	border-radius: 50%;
	border: 1px solid #555;
	padding: 5px;
`;

const PresetGrid = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	justify-content: space-between;
`;

const PresetItem = styled.div`
	cursor: pointer;
	padding: 5px;
	border-radius: 4px;
	display: flex;
	align-items: center;
	justify-content: center;

	&.selected {
		background-color: #444;
	}

	&:hover {
		background-color: #333;
	}
`;

const ActionButton = styled.button`
	background-color: #444;
	color: white;
	border: none;
	border-radius: 4px;
	padding: 3px 10px;
	font-size: 11px;
	cursor: pointer;
	height: 22px;

	&:hover {
		background-color: #555;
	}
`;

const SliderRow = styled.div`
	display: flex;
	align-items: center;
`;

const SliderLabel = styled(Typography)`
	min-width: 40px;
	font-size: 11px;
`;

const StyledSlider = styled(Slider)`
	flex: 1;
	margin: 0 17px 0 10px;
	color: #aaa;

	.MuiSlider-rail {
		background-color: #444;
	}

	.MuiSlider-mark {
		background-color: #666;
		height: 3px;
	}

	.MuiSlider-thumb {
		width: 16px;
		height: 16px;
	}
`;

const SizeIndicator = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	background-color: #444;
	color: white;
	font-size: 11px;
	font-weight: bold;
`;

export const ColourWrapper = styled.div`
	.w-color-colorful {
		// color picker
		.w-color-interactive.w-color-saturation.w-color-colorful {
			border-bottom: 1px solid #000 !important;
			height: 40px !important;
			margin-bottom: 14px !important;
		}

		// color slider
		.w-color-alpha.w-color-alpha-horizontal.w-color-hue.w-color-colorful {
			height: 14px !important;
			margin-bottom: 10px !important;
			.w-color-interactive {
				// picker circle
				div {
					// outer white circle (drag handle)
					width: 22px !important;
					height: 22px !important;

					div {
						// inner colored circle
						width: 100% !important;
						height: 100% !important;
					}
				}
			}
		}

		// alpha slider
		.w-color-alpha.w-color-alpha-horizontal.w-color-colorful {
			height: 14px !important;
			.w-color-interactive {
				// picker circle
				div {
					// outer white circle (drag handle)
					width: 22px !important;
					height: 22px !important;

					div {
						// inner colored circle
						width: 100% !important;
						height: 100% !important;
					}
				}
			}
		}
	}
`;
